<template>
  <div>
    <!-- Common error message -->
    <div v-if="error">
      <div class="alert alert-danger text-center">
        {{ error }}
      </div>
    </div>

    <!-- Selection options and filters -->
    <div class="row">
      <div class="col-md-6">
        <!-- Select all adjusters on all pages options -->
        <div v-if="showSelectAllPagesOptions">
          <template v-if="allPagesSelected">
            <template v-if="load.allIds">
              <i class="fa fa-spinner fa-spin"></i> Loading adjusters...
            </template>
            <template v-else>
              <i class="fa fa-check-square"></i> <i><strong><strong>{{ paginationData.total }}</strong> total adjusters selected on all pages</strong></i>
              <a class="btn btn-default" @click="allPagesSelected = false"><i class="fa fa-remove"></i> <i>Deselect</i></a>
            </template>
          </template>
          <template v-else>
            <a class="btn btn-default" @click="allPagesSelected = true"><i class="fa fa-check-square-o"></i> <i>Select all <strong>{{ paginationData.total }}</strong> adjusters</i></a>
            <small><i>(including those not displayed on this page)</i></small>
          </template>
        </div>
      </div>

      <div class="col-md-6 text-right">
        <!--Filter button -->
        <a v-if="displayFilters || load.resetFilters" class="btn btn-danger" @click="resetFilters">
          <i class="fa fa-eraser"></i> Reset filters
          <template v-if="load.resetFilters"><i class="fa fa-spinner fa-spin"></i></template>
        </a>
        <a v-else class="btn btn-default" @click="displayFilters = true"><i class="fa fa-filter"></i> Filter adjusters</a>
      </div>
    </div>

    <!-- Filters -->
    <div v-if="displayFilters || load.resetFilters" class="card mb-0 mt-1">
      <div class="card-body form-inline justify-content-center">

        <div v-for="(filter, filterName) in filterConfig" class="form-group">
          <label :for="'filter-' + filterName">{{ filter.label }}</label>

          <input type="text" v-if="filter.type === 'text'" v-model="filters[filterName]" class="form-control mx-sm-3" :id="'filter-' + filterName" @keydown.enter.prevent="applyFilters">

          <select v-if="filter.type === 'select'" v-model="filters[filterName]" class="form-control mx-sm-3" :id="'filter-' + filterName">
            <option v-for="option in filter.options" :value="option.key">{{ option.value }}</option>
          </select>
        </div>

        <a class="btn btn-secondary" @click="applyFilters">
          Apply
          <template v-if="load.applyFilters"><i class="fa fa-spinner fa-spin"></i></template>
        </a>
      </div>
    </div>

    <div v-if="load.adjusters" class="text-center mt-2">
      <!-- Loading indicator -->
      <i class="fa fa-spinner fa-spin fa-3x"></i>
    </div>
    <div v-else>

      <!-- Adjuster list -->
      <div v-if="adjusters.length" class="table-responsive">

        <table class="table table-bordered table-hover mb-1 mt-2">

          <thead>
          <tr>
            <th v-if="allowSelectAdjusters" class="text-center">
              <checkbox-list-toggler
                :ids="adjusters.map(s => s.id)"
                @items-updated="(ids) => selectedIds = ids"
                @all-updated="(all) => allSelected = all">
              </checkbox-list-toggler>
            </th>

            <th v-for="column in columns">
              <a @click="sortBy(column.name)" class="btn btn-outline btn-default d-flex">
                {{ column.label }} <span :class="sortIconClass(column.name)"></span>
              </a>
            </th>
          </tr>
          </thead>

          <tbody>
          <tr v-for="(adjuster, index) in adjusters" :class="adjuster.text_opted_out_at ? 'text-muted bg-grey' : ''">

            <td v-if="allowSelectAdjusters" class="text-center">
              <checkbox-toggler :index="index" :id="adjuster.id">
              </checkbox-toggler>
            </td>

            <td v-for="column in columns" v-html="adjuster[column.key]"></td>
          </tr>
          </tbody>

        </table>

      </div>
      <div v-else>
        <div class="alert alert-warning text-center m-t-xs m-b-xs">
          No adjusters found
        </div>
      </div>

    </div>

    <!-- Bottom pagination block -->
    <pagination :data="paginationData" @page-changed="loadPage">
    </pagination>

  </div>
</template>

<script>
import { mapWritableState } from 'pinia'
import { useTextCampaignAdjusterDataStore } from '../../stores/textCampaignAdjusterData'

export default {

    props: {

      // Text campaign ID.
      textCampaignId: {
        type: Number,
        required: true
      },

      // Allow select adjusters.
      allowSelectAdjusters: {
        type: Boolean,
        required: true
      },

      // A configuration of adjuster list columns.
      columnConfig: {
        type: Object,
        default: () => {return {};}
      },

      // A configuration of adjuster filters.
      filterConfig: {
        type: Object,
        default: () => {return {};}
      },

      // Initial filters' state.
      filterState: {
        type: Object,
        required: true
      },

    },

    /**
     * Variables (models).
     */
    data () {
      return {
        // Adjuster list.
        adjusters: [],
        // Current filters.
        filters: {},
        // Current sorts.
        sorts: {
          'by': 'name',
          'direction': 'asc',
        },
        // Pagination data.
        paginationData: {},
        // Display filters indicator.
        displayFilters: false,
        // Common error message.
        error: '',
        // Loading indication helpers.
        load: {
          adjusters: false,
          pagination: false,
          allIds: false,
          resetFilters: false,
        },
        // Is all adjusters selected regardless of pagination.
        allPagesSelected: false,
        // All adjuster IDs including those not listed on current page.
        allAdjusterIds: [],

        selectedIds: [],
        allSelected: false,
      }
    },

    /**
     * Computed values
     */
    computed: {
      ...mapWritableState(useTextCampaignAdjusterDataStore, ['selectedAdjusterIds']),

      showSelectAllPagesOptions() {
        return this.allowSelectAdjusters && this.allSelected && (this.paginationData.last_page !== 1);
      },

      columns() {
        let columns = [];

        Object.entries(this.columnConfig).forEach(entry => {
          const [columnName, config] = entry;
          columns.push({
            'name': columnName,
            'label': config.label,
            'key': config.value ? config.value : columnName,
          });
        });

        return columns;
      }

    },

    /**
     * Functions (methods).
     */
    methods: {

      /**
       * Init component.
       */
      init() {
        this.initializeFilters();
        this.loadAdjusters();
      },

      /**
       * Generate FormData.
       */
      formData() {
        const formData = new FormData();
        formData.append('_token', this.$csrf_token);
        formData.append('display_filters', JSON.stringify(this.displayFilters));
        formData.append('filters', JSON.stringify(this.filters));
        formData.append('sorts', JSON.stringify(this.sorts));
        return formData;
      },

      /**
       * Pagination - load page.
       */
      loadPage(page) {
        this.loadAdjusters(page);
      },

      /**
       * Load adjusters according to current filters and pagination.
       */
      loadAdjusters(page) {
        page = page || 1;
        this.error = '';
        this.allPagesSelected = false;

        let formData = this.formData();
        this.load.adjusters = true;
        axios.post('/adjusters/text-campaigns/' + this.textCampaignId + '/adjusters?page=' + page, formData, formData)
          .then(response => {
            this.adjusters = response.data.data;
            this.paginationData = response.data;
          })
          .catch(error => {
            this.handleErrors(error, 'Unable to load adjusters.')
          })
          .then(() => {
            this.load.adjusters = false;
            this.load.resetFilters = false;
            this.load.applyFilters = false;
          });
      },

      /**
       * Load adjuster IDs according to current filters but without a pagination.
       */
      loadAllAdjusterIds() {
        this.error = '';
        if (this.allAdjusterIds.length) {
          this.selectedAdjusterIds = this.allAdjusterIds;
          return;
        }

        let formData = this.formData();
        this.load.allIds = true;
        axios.post('/adjusters/text-campaigns/' + this.textCampaignId + '/adjuster-ids', formData, formData)
          .then(response => {
            this.allAdjusterIds = response.data;
            this.selectedAdjusterIds = this.allAdjusterIds;
          })
          .catch(error => {
            this.handleErrors(error, 'Unable to load adjusters.')
          })
          .then(() => {
            this.load.allIds = false;
          });
      },

      /**
       * Sort adjusters by specified column/direction.
       */
      sortBy(by, direction = 'toggle') {
        if (direction === 'toggle') {
          if (by === this.sorts.by) {
            direction = this.sorts.direction === 'asc' ? 'desc' : 'asc';
          } else direction = 'asc';
        }

        this.sorts.by = by;
        this.sorts.direction = direction;
        this.loadAdjusters();
      },

      /**
       * Sort icon class to show near the column name.
       */
      sortIconClass(column) {
        return this.sorts.by === column ? 'fa fa-sort-' + this.sorts.direction : '';
      },

      /**
       * Initialize filters on startup based on filterState prop.
       */
      initializeFilters() {
        this.displayFilters = this.filterState.display_filters ?? false;
        if (Object.keys(this.filterState.filters).length === 0) {
          this.loadDefaultFilters();
        } else {
          this.filters = this.filterState.filters;
        }
        if (Object.keys(this.filterState.sorts).length === 0) {
          this.loadDefaultSorts();
        } else {
          this.sorts = this.filterState.sorts;
        }
      },

      /**
       * Load default filter values.
       */
      loadDefaultFilters() {
        let defaultValues = {};

        Object.entries(this.filterConfig).forEach(entry => {
          const [filterName, config] = entry;
          defaultValues[filterName] = config.value;
        });

        this.filters = _.cloneDeep(defaultValues);
      },

      /**
       * Load default sort values.
       */
      loadDefaultSorts() {
        this.sorts = {
          'by': 'name',
          'direction': 'asc',
        };
      },

      /**
       * Reset filters.
       */
      resetFilters() {
        this.loadDefaultFilters();
        this.loadDefaultSorts();

        this.displayFilters = false;

        this.load.resetFilters = true;
        this.resetAllSelectedAdjusters();
        this.loadAdjusters();
      },

      /**
       * Apply filters.
       */
      applyFilters() {
        this.load.applyFilters = true;
        this.resetAllSelectedAdjusters();
        this.loadAdjusters();
      },

      /**
       * Reset all selected adjusters on all pages.
       */
      resetAllSelectedAdjusters() {
        this.allAdjusterIds = [];
      },

      /**
       * Handle selection changed events.
       */
      selectionChanged() {
        this.selectedAdjusterIds = this.selectedIds;
        if (this.selectedIds.length < this.adjusters.length) {
          this.allPagesSelected = false;
        }
        if (this.allPagesSelected) {
          this.loadAllAdjusterIds();
        }
      },

      /**
       * Handle axios errors
       *
       * @param errorObj
       * @param defaultMessage
       */
      handleErrors(errorObj, defaultMessage) {
        if (errorObj.response.status === 403) {
          this.error = defaultMessage + ': access denied.';
        } else {
          this.error = defaultMessage;
        }
      },

    },

    watch: {
      selectedIds: {
        immediate: true,
        deep: true,
        handler () {
          this.selectionChanged();
        }
      },
      allPagesSelected: {
        immediate: true,
        handler () {
          this.selectionChanged();
        }
      },
    },

    /**
     * Component initialization.
     */
    mounted: function () {
      this.init();
    },
  }
</script>
